"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { fetchCurrentUser } from "@/lib/api/me-api";
import { getApiConfig } from "@/lib/api/config";
import {
  AUTH_SESSION_CHANGED_EVENT,
  isAuthenticated,
} from "@/lib/api/auth-token";
import { initializeAuth, signOutAndReload } from "@/lib/auth";
import {
  hasSamlHandoffParams,
  isSamlCallbackPath,
} from "@/lib/auth/saml-config";
import { isPublicPath } from "@/lib/auth/public-paths";
import { buildMockAuthUser } from "@/lib/auth/mock-user";
import { getAuthProfile, storeAuthProfile } from "@/lib/auth/profile";
import type { AuthApiUser } from "@/types/auth-api-types";
import type { AuthProfile } from "@/types/auth-types";

type AuthUserContextValue = {
  user: AuthApiUser | null;
  profile: AuthProfile | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
};

const AuthUserContext = createContext<AuthUserContextValue | null>(null);

function userToProfile(user: AuthApiUser): AuthProfile {
  return {
    name: user.name,
    email: user.email,
  };
}

function mockUserFromStorage(): AuthApiUser | null {
  const profile = getAuthProfile();
  if (!profile) return null;
  return buildMockAuthUser(profile);
}

export function AuthUserProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const { useMock } = getApiConfig();
  // const [sessionVersion, setSessionVersion] = useState(0);
  const [authInitialized, setAuthInitialized] = useState(false);
  const redirectedOnMeError = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function prepareSession() {
      if (!hasSamlHandoffParams()) {
        await initializeAuth();
      }
      if (!cancelled) {
        // setSessionVersion((v) => v + 1);
        setAuthInitialized(true);
      }
    }

    void prepareSession();

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  useEffect(() => {
    const onSessionChange = () => {
      // setSessionVersion((v) => v + 1);
      if (isAuthenticated()) {
        void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      } else {
        queryClient.removeQueries({ queryKey: ["auth", "me"] });
      }
    };
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, onSessionChange);
    return () =>
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, onSessionChange);
  }, [queryClient]);

  const [hasToken, setHasToken] = useState(() => isAuthenticated());

  useEffect(() => {
    const syncToken = () => setHasToken(isAuthenticated());
    syncToken();
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, syncToken);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, syncToken);
  }, [pathname]);

  const {
    data,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const user = await fetchCurrentUser();
      storeAuthProfile(userToProfile(user));
      return user;
    },
    enabled: authInitialized && hasToken && !useMock,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (
      !isError ||
      !hasToken ||
      useMock ||
      !authInitialized ||
      isPublicPath(pathname) ||
      isSamlCallbackPath(pathname)
    ) {
      return;
    }

    if (redirectedOnMeError.current) return;
    redirectedOnMeError.current = true;
    void signOutAndReload();
  }, [
    isError,
    hasToken,
    useMock,
    authInitialized,
    pathname,
  ]);

  const mockUser = useMock && hasToken ? mockUserFromStorage() : null;
  const user = hasToken ? (useMock ? mockUser : (data ?? null)) : null;

  const profile = useMemo(
    () => (user ? userToProfile(user) : null),
    [user],
  );

  const refresh = useCallback(async () => {
    if (!isAuthenticated()) {
      queryClient.removeQueries({ queryKey: ["auth", "me"] });
      return;
    }
    if (useMock) return;
    await refetch();
  }, [queryClient, refetch, useMock]);

  const value = useMemo<AuthUserContextValue>(
    () => ({
      user,
      profile,
      isLoading: hasToken && !useMock && isPending,
      isError,
      error: error instanceof Error ? error : null,
      refresh,
    }),
    [
      user,
      profile,
      hasToken,
      useMock,
      isPending,
      isError,
      error,
      refresh,
    ],
  );

  return (
    <AuthUserContext.Provider value={value}>{children}</AuthUserContext.Provider>
  );
}

export function useAuthUser(): AuthUserContextValue {
  const context = useContext(AuthUserContext);
  if (!context) {
    throw new Error("useAuthUser must be used within AuthUserProvider");
  }
  return context;
}

/** Seed React Query cache after callback /me (avoids refetch flash on dashboard). */
export function primeAuthUserInContext(
  queryClient: QueryClient,
  user: AuthApiUser,
): void {
  storeAuthProfile(userToProfile(user));
  queryClient.setQueryData(["auth", "me"], user);
}
